/* PERROWALL cookie consent banner.
 * Single source of truth: Shopify Customer Privacy API (setTrackingConsent).
 * Google Consent Mode v2 updates are driven by the "visitorConsentCollected"
 * document event (see snippets/pw-consent-mode.liquid); this script only calls
 * window.__pwConsentMode.update() directly as a fallback when the Customer
 * Privacy API is unavailable. Fail-safe: no API -> everything stays denied.
 * No dependency, defer-loaded, CNIL-compliant (refuse = same effort as accept).
 */
(function () {
  'use strict';

  var COOKIE_NAME = 'ab_consent';
  var COOKIE_MAX_AGE = 182 * 24 * 60 * 60; /* 6 months (CNIL: re-ask after 6 months, accept or refuse) */
  var API_TIMEOUT_MS = 3000;

  var root = document.getElementById('pw-consent-root');
  if (!root) return;

  var cfg;
  try {
    cfg = JSON.parse(document.getElementById('pw-consent-config').textContent);
  } catch (e) {
    return; /* malformed config: do nothing, trackers stay denied by default */
  }

  var dialog = root.querySelector('.pw-consent__dialog');
  var overlay = root.querySelector('.pw-consent__overlay');
  var prefsPanel = root.querySelector('.pw-consent__prefs');
  var openedFromFooter = false;
  var lastFocused = null;

  /* ---------- cookie helpers ---------- */

  function readCookie(name) {
    var parts = ('; ' + document.cookie).split('; ' + name + '=');
    if (parts.length < 2) return null;
    try {
      return JSON.parse(decodeURIComponent(parts.pop().split(';').shift()));
    } catch (e) {
      return null;
    }
  }

  function writeCookie(name, value) {
    var secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      name + '=' + encodeURIComponent(JSON.stringify(value)) +
      '; Max-Age=' + COOKIE_MAX_AGE + '; Path=/; SameSite=Lax' + secure;
  }

  /* ---------- Customer Privacy API ---------- */

  function loadPrivacyApi(callback) {
    if (window.Shopify && window.Shopify.customerPrivacy) {
      callback(window.Shopify.customerPrivacy);
      return;
    }
    var done = false;
    var finish = function (api) {
      if (done) return;
      done = true;
      callback(api || null);
    };
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      try {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function (error) {
            finish(!error && window.Shopify.customerPrivacy ? window.Shopify.customerPrivacy : null);
          }
        );
      } catch (e) {
        finish(null);
      }
    }
    setTimeout(function () { finish(null); }, API_TIMEOUT_MS);
  }

  /* ---------- consent application ---------- */

  function applyConsent(consent, api, onDone) {
    /* 1. Shopify Customer Privacy = source of truth. Propagates to Shopify
       analytics, web pixels (GA4/Meta/TikTok channels) and checkout. */
    var payload = {
      analytics: consent.analytics,
      marketing: consent.marketing,
      preferences: consent.preferences,
      sale_of_data: consent.marketing
    };
    var after = function () {
      /* 2. Consent Mode v2 fallback (the visitorConsentCollected listener is
         the normal path; update() dedupes identical states). */
      if (window.__pwConsentMode) window.__pwConsentMode.update(consent);
      /* 3. First-party record for the 6-month re-prompt rule. */
      writeCookie(COOKIE_NAME, {
        v: cfg.version,
        ts: Date.now(),
        a: consent.analytics ? 1 : 0,
        m: consent.marketing ? 1 : 0,
        p: consent.preferences ? 1 : 0
      });
      if (typeof onDone === 'function') onDone();
    };
    if (api && typeof api.setTrackingConsent === 'function') {
      try {
        api.setTrackingConsent(payload, after);
      } catch (e) {
        after();
      }
    } else {
      after();
    }
  }

  /* ---------- proof-of-consent beacon (art. 7 GDPR accountability) ---------- */

  function sendProof(consent, action) {
    if (!cfg.webhook) return;
    var finish = function (uaHash) {
      var body = JSON.stringify({
        ts: new Date().toISOString(),
        store: cfg.store || 'appy-baby',
        consent_state: {
          analytics: consent.analytics,
          marketing: consent.marketing,
          preferences: consent.preferences
        },
        action: action,
        banner_version: cfg.version,
        copy_variant: cfg.variant,
        page: location.pathname,
        ua_hash: uaHash
      });
      try {
        /* text/plain = CORS-safelisted: no preflight (sendBeacon cannot preflight) */
        if (navigator.sendBeacon) {
          navigator.sendBeacon(cfg.webhook, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
        } else {
          fetch(cfg.webhook, { method: 'POST', body: body, keepalive: true, headers: { 'Content-Type': 'text/plain;charset=UTF-8' } });
        }
      } catch (e) { /* proof logging must never break the storefront */ }
    };
    try {
      var data = new TextEncoder().encode(navigator.userAgent);
      crypto.subtle.digest('SHA-256', data).then(function (buf) {
        var hex = Array.prototype.map.call(new Uint8Array(buf), function (b) {
          return ('0' + b.toString(16)).slice(-2);
        }).join('');
        finish(hex.slice(0, 16)); /* truncated hash: non-identifying */
      }).catch(function () { finish(null); });
    } catch (e) {
      finish(null);
    }
  }

  /* ---------- dialog UI ---------- */

  function focusables() {
    return dialog.querySelectorAll('button, a[href], input:not([disabled])');
  }

  function trapFocus(event) {
    if (event.key === 'Escape' && openedFromFooter) {
      event.preventDefault();
      hide();
      return;
    }
    if (event.key !== 'Tab') return;
    var items = focusables();
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function show(withPrefs, current) {
    lastFocused = document.activeElement;
    root.hidden = false;
    togglePrefs(!!withPrefs);
    if (current) {
      setToggle('analytics', current.analytics);
      setToggle('marketing', current.marketing);
      setToggle('preferences', current.preferences);
    }
    requestAnimationFrame(function () {
      root.classList.add('pw-consent--visible');
    });
    document.addEventListener('keydown', trapFocus);
    var heading = dialog.querySelector('.pw-consent__title');
    if (heading) heading.focus();
  }

  function hide() {
    root.classList.remove('pw-consent--visible');
    document.removeEventListener('keydown', trapFocus);
    setTimeout(function () { root.hidden = true; }, 220);
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    openedFromFooter = false;
  }

  function togglePrefs(open) {
    prefsPanel.hidden = !open;
    var btn = dialog.querySelector('[data-pw-action="customize"]');
    if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function setToggle(name, value) {
    var input = prefsPanel.querySelector('input[data-pw-category="' + name + '"]');
    if (input) input.checked = value === true;
  }

  function readToggles() {
    return {
      analytics: prefsPanel.querySelector('input[data-pw-category="analytics"]').checked,
      marketing: prefsPanel.querySelector('input[data-pw-category="marketing"]').checked,
      preferences: prefsPanel.querySelector('input[data-pw-category="preferences"]').checked
    };
  }

  /* ---------- decision flow ---------- */

  var privacyApi = null;

  function decide(consent, action) {
    if (cfg.designMode) { hide(); return; } /* theme editor preview: never write state */
    applyConsent(consent, privacyApi, null);
    sendProof(consent, action);
    hide();
  }

  function wireButtons() {
    dialog.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-pw-action]');
      if (!btn) return;
      event.preventDefault();
      var action = btn.getAttribute('data-pw-action');
      if (action === 'accept') {
        decide({ analytics: true, marketing: true, preferences: true }, 'accept_all');
      } else if (action === 'refuse') {
        decide({ analytics: false, marketing: false, preferences: false }, 'refuse_all');
      } else if (action === 'customize') {
        togglePrefs(prefsPanel.hidden);
      } else if (action === 'save') {
        decide(readToggles(), 'custom');
      }
    });
    overlay.addEventListener('click', function () {
      if (openedFromFooter) hide(); /* first visit: an explicit answer is required */
    });
  }

  function storedConsent() {
    var c = readCookie(COOKIE_NAME);
    if (!c || typeof c.ts !== 'number') return null;
    if (Date.now() - c.ts > COOKIE_MAX_AGE * 1000) return null;
    return { analytics: c.a === 1, marketing: c.m === 1, preferences: c.p === 1 };
  }

  function currentApiConsent(api) {
    if (!api || typeof api.currentVisitorConsent !== 'function') return null;
    try {
      var c = api.currentVisitorConsent();
      if (!c || (c.analytics === '' && c.marketing === '')) return null;
      return {
        analytics: c.analytics === 'yes',
        marketing: c.marketing === 'yes',
        preferences: c.preferences === 'yes'
      };
    } catch (e) {
      return null;
    }
  }

  function openManager() {
    openedFromFooter = true;
    var current = storedConsent() || currentApiConsent(privacyApi) ||
      { analytics: false, marketing: false, preferences: false };
    show(true, current);
  }

  function init() {
    wireButtons();

    /* Footer "Gérer les cookies" trigger (delegated: works for menu links too) */
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-pw-consent-open], a[href="#gerer-les-cookies"]');
      if (!trigger) return;
      event.preventDefault();
      openManager();
    });
    window.PWConsent = { open: openManager };

    if (cfg.designMode) {
      show(false, null); /* always visible in the theme editor for styling */
      return;
    }

    var stored = storedConsent();

    loadPrivacyApi(function (api) {
      privacyApi = api;
      if (stored) {
        /* Returning visitor: refresh Consent Mode; re-sync Shopify only if its
           own consent cookie expired (avoids a network call on every page). */
        if (window.__pwConsentMode) window.__pwConsentMode.update(stored);
        if (api && currentApiConsent(api) === null) {
          applyConsent(stored, api, null);
        }
        return;
      }
      var apiConsent = currentApiConsent(api);
      if (apiConsent) {
        /* Shopify already has an answer (e.g. set before this banner existed) */
        if (window.__pwConsentMode) window.__pwConsentMode.update(apiConsent);
        writeCookie(COOKIE_NAME, {
          v: cfg.version,
          ts: Date.now(),
          a: apiConsent.analytics ? 1 : 0,
          m: apiConsent.marketing ? 1 : 0,
          p: apiConsent.preferences ? 1 : 0
        });
        return;
      }
      if (cfg.scope === 'required_only' && api &&
          typeof api.shouldShowBanner === 'function') {
        var shouldShow = true;
        try { shouldShow = api.shouldShowBanner() === true; } catch (e) { shouldShow = true; }
        if (!shouldShow) return;
      }
      setTimeout(function () { show(false, null); }, Math.max(0, cfg.delay || 0));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
